/**
 * Screen 3 — "Can you edit your website". Inviting a Developer (sites,
 * install, verify; never leads) unblocks the signer-upper before
 * they hit the technical wall. "A free page from FinderlyFix"
 * (org setup's "A page from us" lands here with it chosen) turns on the
 * FinderlyFix-hosted page (`finderlyfix.com/p/{slug}`, FR-018, T063): the page
 * mounts the partner's live key, so there is nothing to paste and nothing to
 * verify — Continue skips the Install and Verify steps (plan.md §5, screen 3).
 * The page's address, headline and intro are set in HostedPagePanel.tsx, which
 * Sites & keys also mounts for later edits. Once a page is live (Continue
 * would skip ahead), "Next: complete your page" links to its profile
 * (`/pages/:slug`: phone, services, hours, photos).
 */
import { useState } from 'react';
import { ApiError } from '../api/errors';
import type { HostedPage } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { Link, useRouter } from '../router';
import { OnboardingPage } from '../setup/OnboardingPage';
import { useProgress } from '../setup/progress';
import { Choices, ErrorNotice, Field, Time, useToast } from '../ui/kit';
import { useLocalState, useMutation, useQuery } from '../ui/hooks';
import { HostedPagePanel, loadHostedPages } from './HostedPagePanel';

type Owner = 'self' | 'developer' | 'none';

export function InstallOwner() {
  const { api, org, role } = useSession();
  const [pages, setPages] = useState<HostedPage[]>([]);
  const livePage = pages.find((p) => p.enabled && p.served) ?? null;
  const toast = useToast();
  const { navigate } = useRouter();
  const { markDone, unmarkDone } = useProgress();
  const [owner, setOwnerState] = useLocalState<Owner>(`fx-installer-${org?.id}`, 'self');
  const [email, setEmail] = useState('');
  const [continueError, setContinueError] = useState<ApiError | null>(null);
  // Leaving "no website" undoes the skip it made: Install and Verify are real
  // steps again for someone who will paste the snippet (#888 review finding 8).
  const setOwner = (next: Owner) => {
    if (owner === 'none' && next !== 'none') {
      unmarkDone('install');
      unmarkDone('verify');
    }
    setContinueError(null);
    setOwnerState(next);
    // Choosing is what this step asks for (setup/status.ts).
    markDone('installer');
  };
  const members = useQuery(() => api.listMembers(), [org?.id]);
  const invite = useMutation((addr: string) => api.inviteMember(addr, 'developer'));
  const developers = (members.data?.items ?? []).filter((m) => m.role === 'developer');

  return (
    <OnboardingPage
      slug="installer"
      question={owner === 'none' ? 'Your page' : undefined}
      lede={
        owner === 'none'
          ? 'Pick your address, then add what homeowners look for: your number, services, hours and photos. Nothing to install, so the snippet and verification steps are skipped.'
          : 'The next two steps are technical: a snippet, and a check that it’s live.'
      }
      beforeContinue={async () => {
        if (owner !== 'none') return true;
        // Only skip Install/Verify when the page really exists and is being
        // served; otherwise the partner would reach Branding with nothing live
        // anywhere. A failed read is an error to show, not "no page yet"
        // (#888 review finding 7).
        let pages: HostedPage[];
        try {
          pages = (await loadHostedPages(api)).items;
        } catch (err) {
          setContinueError(err instanceof ApiError ? err : new ApiError({ status: 0, code: 'NETWORK_ERROR' }));
          return false;
        }
        setContinueError(null);
        if (!pages.some((p) => p.enabled)) {
          toast('Create your page first, or choose another option.');
          return false;
        }
        if (!pages.some((p) => p.enabled && p.served)) {
          toast('Your page isn’t showing yet. Enable its live site key, or choose another option.');
          return false;
        }
        markDone('installer');
        markDone('install');
        markDone('verify');
        navigate('/setup/branding');
        return false;
      }}
    >
      <Choices<Owner>
        legend="Who will put the widget on your site?"
        hideLegend
        value={owner}
        onChange={setOwner}
        options={[
          { value: 'self', title: 'I can', description: 'You edit the site yourself, or can paste a snippet into your site builder.' },
          { value: 'developer', title: 'Someone else does', description: 'Invite them as a Developer. They see sites, install and verification — never leads.' },
          {
            value: 'none',
            title: 'A free page from FinderlyFix',
            description: 'We host a page for your brand at finderlyfix.com/p/…, with a link and QR code to share. Nothing to install.',
          },
        ]}
      />
      {owner === 'none' ? <HostedPagePanel onPagesChange={setPages} onboarding /> : null}
      {owner === 'none' && livePage && can(role, 'hostedPageProfile') ? (
        <p className="row" style={{ flexWrap: 'wrap', margin: 0, alignItems: 'center' }}>
          <span>Next:</span>
          <Link className="btn btn-sm" to={`/pages/${encodeURIComponent(livePage.slug)}`}>
            Complete your page
          </Link>
          <span className="small muted">Add your phone number, services, hours and photos — now or any time from Your page.</span>
        </p>
      ) : null}
      <ErrorNotice error={continueError} />
      {owner === 'developer' ? (
        <section className="panel panel-pad stack">
          <form
            className="row"
            style={{ alignItems: 'flex-end' }}
            onSubmit={async (e) => {
              e.preventDefault();
              const res = await invite.run(email.trim());
              if (res) {
                toast(`Invited ${email.trim()} as Developer`);
                setEmail('');
                void members.reload();
              }
            }}
          >
            <div className="grow">
              <Field label="Developer’s email" hint="They sign in at this portal with that address; the code arrives by email.">
                {(p) => <input {...p} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dev@agency.com" />}
              </Field>
            </div>
            <button className="btn btn-primary" type="submit" disabled={invite.pending || !/.+@.+\..+/.test(email)}>
              {invite.pending ? 'Inviting…' : 'Invite'}
            </button>
          </form>
          <ErrorNotice error={invite.error} />
          {developers.length ? (
            <ul className="stack-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {developers.map((d) => (
                <li key={d.user_id} className="row-between sunk">
                  <span>{d.email}</span>
                  {d.last_login_at ? (
                    <span className="dotstat ok">
                      Signed in <Time iso={d.last_login_at} />
                    </span>
                  ) : (
                    <span className="dotstat warn">Waiting on developer</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </OnboardingPage>
  );
}
